import React, { useCallback } from 'react';
import { ImperativeOverlayView } from '@usepepper/ui/molecules/map/imperative-overlay-view';
import { MAP_Z_INDEX } from '@usepepper/ui/molecules/map/constants';
import { MapMarkerProps } from '@/experiences/sales-portal/components/map/types';
import { Pressable } from 'react-native';
import { useTheme } from '@/contexts/theme-context';

const PIN_SIZE = 28;
const PIN_SIZE_SELECTED = 34;
const RING_THICKNESS = 2;
const RING_THICKNESS_SELECTED = 3;
const CENTER_DOT_RADIUS = 3.2;
const CENTER_DOT_RADIUS_SELECTED = 4;

// Anchor the circle's center on the coordinate.
const pinContainerStyle = (selected: boolean): React.CSSProperties => ({
  position: 'relative',
  transform: 'translate(-50%, -50%)',
  transition: 'transform 100ms ease',
  zIndex: selected ? MAP_Z_INDEX.selectedPin : MAP_Z_INDEX.pin,
});

const pinShadow =
  'drop-shadow(0 4px 8px rgba(15,23,42,0.22)) drop-shadow(0 1px 2px rgba(15,23,42,0.18))';

const pinSvgStyle: React.CSSProperties = {
  display: 'block',
  filter: pinShadow,
};

/**
 * Interactive map pin, rendered as a child of `BaseMapView`.
 */
export const MapMarker: React.FC<MapMarkerProps> = ({
  accessibilityLabel,
  coordinate,
  fill,
  onPress,
  selected,
}) => {
  const theme = useTheme();

  // Native-level guard: React's delegated onClick fires too late to stop
  // the click from reaching Google Maps' own listeners.
  const preventMapHits = useCallback((node: HTMLDivElement | null) => {
    if (node && typeof google !== 'undefined') {
      google.maps.OverlayView.preventMapHitsFrom(node);
    }
  }, []);

  const size = selected ? PIN_SIZE_SELECTED : PIN_SIZE;
  const ring = selected ? RING_THICKNESS_SELECTED : RING_THICKNESS;
  const centerDot = selected ? CENTER_DOT_RADIUS_SELECTED : CENTER_DOT_RADIUS;
  const color = fill ?? theme.sk.color.icon.primary;

  return (
    <ImperativeOverlayView
      position={{
        lat: coordinate.latitude,
        lng: coordinate.longitude,
      }}
    >
      {/* Plain div so pin clicks can't bubble to the map's onClick. */}
      <div
        onClick={(event) => event.stopPropagation()}
        ref={preventMapHits}
        style={pinContainerStyle(!!selected)}
      >
        <Pressable
          accessibilityLabel={accessibilityLabel}
          accessibilityRole="button"
          accessibilityState={{ selected: !!selected }}
          onPress={onPress}
        >
          <svg
            height={size}
            style={pinSvgStyle}
            viewBox="0 0 28 28"
            width={size}
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="14" cy="14" fill="#fff" r="13" />
            <circle cx="14" cy="14" fill={color} r={13 - ring} />
            <circle cx="14" cy="14" fill="#fff" r={centerDot} />
          </svg>
        </Pressable>
      </div>
    </ImperativeOverlayView>
  );
};
